import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Dumbbell, Heart, FlaskConical, Shirt, Search, ExternalLink, ShoppingBag, ArrowLeft } from 'lucide-react';
import ProductRecommendation from '../components/ProductRecommendation';
import { categories, getProductsByCategory, getAllProducts } from '../data/armoryProducts';
import HamburgerMenu from '../components/HamburgerMenu';

const iconMap = {
  Flask: FlaskConical,
  Dumbbell: Dumbbell,
  Shield: Shield,
  Heart: Heart,
  Shirt: Shirt,
};

const Armory = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const allProducts = getAllProducts();
  const displayProducts = activeCategory === 'all'
    ? allProducts
    : getProductsByCategory(activeCategory);

  const filtered = searchTerm
    ? displayProducts.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tags.some(t => t.includes(searchTerm.toLowerCase()))
      )
    : displayProducts;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 pb-20">
      <HamburgerMenu />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-blood-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider" data-testid="armory-title">
              TRAINING ESSENTIALS
            </h1>
            <Shield className="w-8 h-8 text-blood-400" />
          </div>
          <p className="text-stone-500 text-sm tracking-wide">
            Curated gear and supplements to support your training
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input
            type="text"
            placeholder="Search gear..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full glass border border-stone-700 text-white pl-10 pr-4 py-3 rounded-lg text-sm focus:border-blood-600 focus:outline-none"
            data-testid="armory-search"
          />
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide" data-testid="armory-categories">
          <Button
            onClick={() => setActiveCategory('all')}
            size="sm"
            className={activeCategory === 'all'
              ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap shrink-0'
              : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap shrink-0'
            }
          >
            <ShoppingBag className="w-4 h-4 mr-1" /> All ({allProducts.length})
          </Button>
          {categories.map((cat) => {
            const Icon = iconMap[cat.icon] || Shield;
            const count = getProductsByCategory(cat.id).length;
            return (
              <Button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                size="sm"
                className={activeCategory === cat.id
                  ? 'bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold whitespace-nowrap shrink-0'
                  : 'glass border-stone-700 text-stone-400 hover:bg-stone-800 whitespace-nowrap shrink-0'
                }
              >
                <Icon className="w-4 h-4 mr-1" /> {cat.name} ({count})
              </Button>
            );
          })}
        </div>

        {/* Category Description */}
        {activeCategory !== 'all' && (
          <div className="mb-4">
            <p className="text-stone-400 text-sm">
              {categories.find(c => c.id === activeCategory)?.description}
            </p>
          </div>
        )}

        {/* Product Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="armory-products-grid">
            {filtered.map((product) => (
              <ProductRecommendation key={product.id} product={product} variant="full" />
            ))}
          </div>
        ) : (
          <Card className="glass border-stone-800 p-8 text-center">
            <Search className="w-10 h-10 text-stone-600 mx-auto mb-3" />
            <p className="text-stone-400">No gear found matching "{searchTerm}"</p>
          </Card>
        )}

        {/* Disclaimer */}
        <p className="text-stone-600 text-xs text-center mt-8">
          As an Amazon Associate, Anvil & Ash earns from qualifying purchases.
          Product prices and availability are subject to change.
        </p>
      </div>
    </div>
  );
};

export default Armory;
